import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Save, Check, Key, Copy, Trash2, AlertTriangle, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { apiClient, TenantSettingsResponse, TaxSettings, TaxMode, MissingDataAction, LocationMissingAction, HubSpotPipeline, HubspotOauthStatus, StripeOauthStatus } from '../services/api';
import { StripeIcon } from '../components/icons/LogoIcons';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_COLLECTION_SETTINGS = {
  collection_method: 'send_invoice',
  days_until_due: 30,
  payment_behavior: 'default_incomplete',
};

const DEFAULT_TAX_SETTINGS: TaxSettings = {
  mode: 'manual',
  on_missing_tax_id: 'warn',
  on_missing_tax_rate: 'warn',
  on_missing_customer_location: 'warn',
};

const DEFAULT_ALLOWED_DEAL_STAGES: string[] = ['closedwon'];

// Settings is split into two sub-tabs. The active tab is reflected in the URL
// (?tab=connections | ?tab=sync) so it can be deep-linked — e.g. the app-wide
// "connection not set up" banner links straight to the connections tab.
const SETTINGS_TABS = ['sync', 'connections'] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];
const DEFAULT_SETTINGS_TAB: SettingsTab = 'sync';

interface PolicyOption<T extends string> {
  value: T;
  label: string;
  description: string;
}

const TAX_POLICY_OPTIONS: ReadonlyArray<PolicyOption<MissingDataAction>> = [
  { value: 'skip', label: 'Skip silently', description: 'Continue the sync and emit no warning.' },
  { value: 'warn', label: 'Warn', description: 'Sync continues, warning appears on the deal.' },
  { value: 'block', label: 'Block', description: 'Sync fails until the data is fixed in HubSpot.' },
];

// Stripe Tax needs a location to compute anything, so "skip" would mean
// invoicing no tax at all — it isn't offered here.
const LOCATION_POLICY_OPTIONS: ReadonlyArray<PolicyOption<LocationMissingAction>> = [
  { value: 'warn', label: 'Warn', description: 'Sync continues; the invoice will not collect until an address is added.' },
  { value: 'block', label: 'Block', description: 'Sync fails until an address is available.' },
];

const TAX_MODE_OPTIONS: ReadonlyArray<PolicyOption<TaxMode>> = [
  {
    value: 'manual',
    label: 'Mirror HubSpot tax rates',
    description: "We create a Stripe tax rate for each HubSpot tax rate group and attach it per line.",
  },
  {
    value: 'automatic',
    label: 'Let Stripe calculate tax (Stripe Tax)',
    description: "Stripe computes tax at invoice time from the customer's address. HubSpot tax rates are ignored.",
  },
];

interface TaxPolicyGroupProps<T extends string> {
  label: string;
  helpText: string;
  value: T;
  onChange: (v: T) => void;
  name: string;
  options?: ReadonlyArray<PolicyOption<T>>;
}

function TaxPolicyGroup<T extends string>({
  label,
  helpText,
  value,
  onChange,
  name,
  options,
}: TaxPolicyGroupProps<T>) {
  const choices = (options ?? (TAX_POLICY_OPTIONS as unknown as ReadonlyArray<PolicyOption<T>>));
  return (
    <div className="space-y-2">
      <div className="font-medium text-sm">{label}</div>
      <p className="text-xs text-muted-foreground">{helpText}</p>
      <RadioGroup
        name={name}
        value={value}
        onValueChange={(next) => onChange(next as T)}
        className="space-y-2 pt-1"
      >
        {choices.map((opt) => (
          <div key={opt.value} className="flex items-center">
            <RadioGroupItem
              value={opt.value}
              id={`${name}-${opt.value}`}
              className="mr-2"
            />
            <Label htmlFor={`${name}-${opt.value}`} className="cursor-pointer font-normal">
              <span className="text-sm">{opt.label}</span>
              <span className="ml-2 text-xs text-muted-foreground">— {opt.description}</span>
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

const Settings: React.FC = () => {
  const { tenant } = useAuth();
  const isTestMode = tenant?.is_test ?? true;
  const queryClient = useQueryClient();

  // Keep the app-wide connection banner (useConnectionStatus) in sync after a
  // connection changes here, so it clears/appears immediately instead of
  // waiting for its cache to go stale.
  const refreshConnectionStatus = () => {
    queryClient.invalidateQueries({ queryKey: ['hubspot-oauth-status'] });
    queryClient.invalidateQueries({ queryKey: ['stripe-oauth-status'] });
  };

  // Active sub-tab is driven by the ?tab= query param so it stays deep-linkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: SettingsTab = SETTINGS_TABS.includes(tabParam as SettingsTab)
    ? (tabParam as SettingsTab)
    : DEFAULT_SETTINGS_TAB;

  const handleTabChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', value);
        return next;
      },
      { replace: true },
    );
  };

  const [stripeApiKey, setStripeApiKey] = useState('');
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [stripeSaved, setStripeSaved] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeKeyWarnings, setStripeKeyWarnings] = useState<string[]>([]);
  const [showPermissions, setShowPermissions] = useState(false);
  const [hubspotStatus, setHubspotStatus] = useState<HubspotOauthStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const pollRef = useRef<number | null>(null);
  const [stripeOauthStatus, setStripeOauthStatus] = useState<StripeOauthStatus | null>(null);
  const [stripeStatusError, setStripeStatusError] = useState<string | null>(null);
  const [stripeConnectError, setStripeConnectError] = useState<string | null>(null);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [stripeMode, setStripeMode] = useState<'live' | 'test' | 'sandbox'>('test');
  const stripePollRef = useRef<number | null>(null);
  const [settings, setSettings] = useState<TenantSettingsResponse | null>(null);
  const [collectionSettings, setCollectionSettings] = useState(DEFAULT_COLLECTION_SETTINGS);
  const [daysUntilDueInput, setDaysUntilDueInput] = useState('30');
  const [collectionSaved, setCollectionSaved] = useState(false);
  const [taxSettings, setTaxSettings] = useState<TaxSettings>(DEFAULT_TAX_SETTINGS);
  const [taxSaved, setTaxSaved] = useState(false);
  const [allowedStages, setAllowedStages] = useState<string[]>(DEFAULT_ALLOWED_DEAL_STAGES);
  const [primaryContactRoles, setPrimaryContactRoles] = useState<string[]>(['Billing Contact', 'Decision Maker']);
  const [contactRolesSaved, setContactRolesSaved] = useState<boolean>(false);
  const [pipelines, setPipelines] = useState<HubSpotPipeline[] | null>(null);
  const [pipelinesError, setPipelinesError] = useState<string | null>(null);
  const [pipelinesLoading, setPipelinesLoading] = useState<boolean>(false);
  const [stagesSaved, setStagesSaved] = useState<boolean>(false);
  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const [apiKeyGenerating, setApiKeyGenerating] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);

  // Fetch settings on mount and when tenant changes (sidebar switcher)
  useEffect(() => {
    setSettings(null);
    setStripeApiKey('');
    setCollectionSettings(DEFAULT_COLLECTION_SETTINGS);
    setDaysUntilDueInput('30');
    setTaxSettings(DEFAULT_TAX_SETTINGS);

    apiClient.getTenantSettings()
      .then((data) => {
        setSettings(data);
        const cs = data.collection_settings ?? DEFAULT_COLLECTION_SETTINGS;
        setCollectionSettings(cs);
        setDaysUntilDueInput(String(cs.days_until_due));
        setTaxSettings(data.tax_settings ?? DEFAULT_TAX_SETTINGS);
        setAllowedStages(data.allowed_deal_stages ?? DEFAULT_ALLOWED_DEAL_STAGES);
        setPrimaryContactRoles(data.contact_settings?.primary_contact_roles ?? ['Billing Contact', 'Decision Maker']);
      })
      .catch((err) => console.error('Failed to fetch settings:', err));
  }, [tenant?.id]);

  useEffect(() => {
    setPipelines(null);
    setPipelinesError(null);
    setPipelinesLoading(true);
    apiClient.getHubSpotPipelines()
      .then((data) => setPipelines(data))
      .catch((err) => {
        console.error('Failed to load HubSpot pipelines', err);
        setPipelinesError("Couldn't load HubSpot pipelines. Verify your HubSpot API key, then retry.");
      })
      .finally(() => setPipelinesLoading(false));
  }, [tenant?.id]);

  // Refetch on mount AND when the tenant changes (sidebar switcher), so the
  // HubSpot card reflects the active tenant, not a stale one.
  useEffect(() => {
    setHubspotStatus(null);
    setStatusError(null);
    apiClient.getHubspotOauthStatus()
      .then((status) => { setHubspotStatus(status); setStatusError(null); })
      .catch(() => setStatusError("Couldn't load HubSpot connection status. Refresh to retry."));
  }, [tenant?.id]);

  useEffect(() => () => {
    if (pollRef.current !== null) window.clearInterval(pollRef.current);
  }, []);

  // Refetch on mount AND when the tenant changes (sidebar switcher), so the
  // Stripe App card reflects the active tenant, not a stale one.
  useEffect(() => {
    setStripeOauthStatus(null);
    setStripeStatusError(null);
    apiClient.getStripeOauthStatus()
      .then((status) => { setStripeOauthStatus(status); setStripeStatusError(null); })
      .catch(() => setStripeStatusError("Couldn't load Stripe connection status. Refresh to retry."));
  }, [tenant?.id]);

  useEffect(() => () => {
    if (stripePollRef.current !== null) window.clearInterval(stripePollRef.current);
  }, []);

  const handleSaveStripeKey = async () => {
    setStripeError(null);
    setStripeKeyWarnings([]);
    try {
      const saved = await apiClient.updateTenantSettings({
        stripe_keys: { api_key: stripeApiKey },
      });
      setStripeKeyWarnings(saved?.stripe_key_warnings ?? []);
      setStripeSaved(true);
      setStripeApiKey('');
      const updated = await apiClient.getTenantSettings();
      setSettings(updated);
      refreshConnectionStatus();
      setTimeout(() => setStripeSaved(false), 2000);
    } catch (err: any) {
      const message = err.response?.data?.error || err.response?.data?.detail;
      setStripeError(typeof message === 'string' ? message : 'Failed to save Stripe key');
    }
  };

  const handleConnectHubspot = async () => {
    setConnectError(null);
    setConnecting(true);
    if (pollRef.current !== null) window.clearInterval(pollRef.current);
    try {
      const { url } = await apiClient.getHubspotOauthInstallUrl();
      window.open(url, '_blank', 'noopener');
      const startedAt = Date.now();
      let consecutiveFailures = 0;
      const stopPolling = () => {
        if (pollRef.current !== null) window.clearInterval(pollRef.current);
        pollRef.current = null;
        setConnecting(false);
      };
      pollRef.current = window.setInterval(async () => {
        try {
          const status = await apiClient.getHubspotOauthStatus();
          consecutiveFailures = 0;
          setHubspotStatus(status);
          if (status.connected) refreshConnectionStatus();
          if (status.connected || Date.now() - startedAt > 120_000) {
            stopPolling();
          }
        } catch {
          // Give up if the status endpoint keeps failing, rather than polling
          // a dead backend for the full 2 minutes.
          consecutiveFailures += 1;
          if (consecutiveFailures >= 5) {
            stopPolling();
            setConnectError("Lost connection while checking HubSpot status. Please try again.");
          }
        }
      }, 3000);
    } catch (err: unknown) {
      setConnecting(false);
      setConnectError('Failed to start the HubSpot connection. Please try again.');
    }
  };

  const handleConnectStripe = async () => {
    setStripeConnectError(null);
    setStripeConnecting(true);
    if (stripePollRef.current !== null) window.clearInterval(stripePollRef.current);
    try {
      const { url } = await apiClient.getStripeOauthInstallUrl(stripeMode);
      window.open(url, '_blank', 'noopener');
      const startedAt = Date.now();
      let consecutiveFailures = 0;
      const stopPolling = () => {
        if (stripePollRef.current !== null) window.clearInterval(stripePollRef.current);
        stripePollRef.current = null;
        setStripeConnecting(false);
      };
      stripePollRef.current = window.setInterval(async () => {
        try {
          const status = await apiClient.getStripeOauthStatus();
          consecutiveFailures = 0;
          setStripeOauthStatus(status);
          if (status.connected) refreshConnectionStatus();
          if (status.connected || Date.now() - startedAt > 120_000) {
            stopPolling();
          }
        } catch {
          // Give up if the status endpoint keeps failing, rather than polling
          // a dead backend for the full 2 minutes.
          consecutiveFailures += 1;
          if (consecutiveFailures >= 5) {
            stopPolling();
            setStripeConnectError("Lost connection while checking Stripe status. Please try again.");
          }
        }
      }, 3000);
    } catch (err: unknown) {
      setStripeConnecting(false);
      setStripeConnectError('Failed to start the Stripe connection. Please try again.');
    }
  };

  const handleSaveCollectionSettings = async () => {
    try {
      const daysValue = parseInt(daysUntilDueInput, 10);
      const settingsToSave = {
        ...collectionSettings,
        // 0 is valid (invoice due immediately); only NaN/negative falls back to 30.
        days_until_due: isNaN(daysValue) || daysValue < 0 ? 30 : daysValue,
      };
      await apiClient.updateTenantSettings({
        collection_settings: settingsToSave,
      });
      setCollectionSaved(true);
      const updated = await apiClient.getTenantSettings();
      setSettings(updated);
      const cs = updated.collection_settings ?? DEFAULT_COLLECTION_SETTINGS;
      setCollectionSettings(cs);
      setDaysUntilDueInput(String(cs.days_until_due));
      setTimeout(() => setCollectionSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save collection settings:', err);
    }
  };

  const handleSaveTaxSettings = async () => {
    try {
      await apiClient.updateTenantSettings({
        tax_settings: taxSettings,
      });
      setTaxSaved(true);
      const updated = await apiClient.getTenantSettings();
      setSettings(updated);
      setTaxSettings(updated.tax_settings ?? DEFAULT_TAX_SETTINGS);
      setTimeout(() => setTaxSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save tax settings:', err);
    }
  };

  const handleSaveAllowedStages = async () => {
    try {
      await apiClient.updateTenantSettings({
        allowed_deal_stages: allowedStages,
      });
      const updated = await apiClient.getTenantSettings();
      setSettings(updated);
      setAllowedStages(updated.allowed_deal_stages ?? DEFAULT_ALLOWED_DEAL_STAGES);
      setStagesSaved(true);
      setTimeout(() => setStagesSaved(false), 1500);
    } catch (err) {
      console.error('Failed to save allowed deal stages', err);
    }
  };

  const hasStageChanges = settings
    ? JSON.stringify(allowedStages) !== JSON.stringify(settings.allowed_deal_stages ?? DEFAULT_ALLOWED_DEAL_STAGES)
    : false;

  const handleSaveContactRoles = async () => {
    try {
      await apiClient.updateTenantSettings({
        contact_settings: { primary_contact_roles: primaryContactRoles },
      });
      const updated = await apiClient.getTenantSettings();
      setSettings(updated);
      setPrimaryContactRoles(updated.contact_settings?.primary_contact_roles ?? ['Billing Contact', 'Decision Maker']);
      setContactRolesSaved(true);
      setTimeout(() => setContactRolesSaved(false), 1500);
    } catch (err) {
      console.error('Failed to save contact roles', err);
    }
  };

  const hasContactRolesChanges = settings
    ? JSON.stringify(primaryContactRoles) !== JSON.stringify(settings.contact_settings?.primary_contact_roles ?? ['Billing Contact', 'Decision Maker'])
    : false;

  const handleGenerateApiKey = async () => {
    setApiKeyError(null);
    setApiKeyGenerating(true);
    try {
      const result = await apiClient.generateStripeAppApiKey();
      setGeneratedApiKey(result.stripe_app_api_key);
      const updated = await apiClient.getTenantSettings();
      setSettings(updated);
    } catch (err: any) {
      const message = err.response?.data?.detail;
      setApiKeyError(typeof message === 'string' ? message : 'Failed to generate API key');
    } finally {
      setApiKeyGenerating(false);
    }
  };

  const handleRevokeApiKey = async () => {
    setApiKeyError(null);
    try {
      await apiClient.revokeStripeAppApiKey();
      setGeneratedApiKey(null);
      const updated = await apiClient.getTenantSettings();
      setSettings(updated);
    } catch (err: any) {
      const message = err.response?.data?.detail;
      setApiKeyError(typeof message === 'string' ? message : 'Failed to revoke API key');
    }
  };

  const handleCopyApiKey = () => {
    if (generatedApiKey) {
      navigator.clipboard.writeText(generatedApiKey);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    }
  };

  const hasCollectionChanges = settings
    ? JSON.stringify(collectionSettings) !== JSON.stringify(settings.collection_settings)
    : false;

  const hasTaxChanges = settings
    ? JSON.stringify(taxSettings) !== JSON.stringify(settings.tax_settings ?? DEFAULT_TAX_SETTINGS)
    : false;

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure your API keys for HubSpot and Stripe integrations.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="sync">Sync Details</TabsTrigger>
            <TabsTrigger value="connections">Connections</TabsTrigger>
          </TabsList>

          {/* ============ Sync Details tab ============ */}
          <TabsContent value="sync" className="space-y-6">

        {/* Collection Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>Collection Settings</CardTitle>
            <CardDescription>
              Choose how Stripe collects payment for subscriptions.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup
              value={collectionSettings.collection_method}
              onValueChange={(value) =>
                setCollectionSettings({ ...collectionSettings, collection_method: value })
              }
              className="space-y-3"
            >
              <div className="flex items-center">
                <RadioGroupItem value="send_invoice" id="cm-send-invoice" className="mr-2" />
                <Label htmlFor="cm-send-invoice" className="text-sm font-normal cursor-pointer">
                  Send Invoice
                  <span className="ml-2 text-xs text-muted-foreground">
                    — Invoices are sent to the customer for manual payment
                  </span>
                </Label>
              </div>
              <div className="flex items-center">
                <RadioGroupItem value="charge_automatically" id="cm-charge-auto" className="mr-2" />
                <Label htmlFor="cm-charge-auto" className="text-sm font-normal cursor-pointer">
                  Charge Automatically
                  <span className="ml-2 text-xs text-muted-foreground">
                    — Automatically charges the customer's payment method
                  </span>
                </Label>
              </div>
              <div className="flex items-center">
                <RadioGroupItem value="send_invoice_then_auto" id="cm-send-then-auto" className="mr-2" />
                <Label htmlFor="cm-send-then-auto" className="text-sm font-normal cursor-pointer">
                  Send Invoice → Auto-Charge
                  <span className="ml-2 text-xs text-muted-foreground">
                    — Invoices the customer first, then auto-charges the saved card on later cycles.
                  </span>
                </Label>
              </div>
            </RadioGroup>

            {/* Sub-options based on collection method */}
            {(collectionSettings.collection_method === 'send_invoice' || collectionSettings.collection_method === 'send_invoice_then_auto') && (
              <div className="space-y-2 pt-2 border-t">
                <Label htmlFor="days-until-due">Days Until Due</Label>
                <Input
                  id="days-until-due"
                  type="text"
                  inputMode="numeric"
                  value={daysUntilDueInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setDaysUntilDueInput(val);
                    const parsed = parseInt(val, 10);
                    if (!isNaN(parsed)) {
                      setCollectionSettings({ ...collectionSettings, days_until_due: parsed });
                    }
                  }}
                  onBlur={() => {
                    const parsed = parseInt(daysUntilDueInput, 10);
                    // 0 is valid (invoice due immediately); only empty/negative resets to 30.
                    if (isNaN(parsed) || parsed < 0) {
                      setDaysUntilDueInput('30');
                      setCollectionSettings({ ...collectionSettings, days_until_due: 30 });
                    }
                  }}
                  className="w-32"
                />
                <p className="text-xs text-muted-foreground">
                  Number of days the customer has to pay the invoice (default: 30). Use 0 for due immediately.
                </p>
              </div>
            )}

            {collectionSettings.collection_method === 'charge_automatically' && (
              <div className="space-y-2 pt-2 border-t">
                <Label htmlFor="payment-behavior">Payment Behavior</Label>
                <select
                  id="payment-behavior"
                  value={collectionSettings.payment_behavior}
                  onChange={(e) => setCollectionSettings({
                    ...collectionSettings,
                    payment_behavior: e.target.value,
                  })}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                >
                  <option value="default_incomplete">Default Incomplete</option>
                  <option value="error_if_incomplete">Error If Incomplete</option>
                  <option value="allow_incomplete">Allow Incomplete</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  How Stripe handles a failed payment on the subscription
                </p>
              </div>
            )}

            <Button
              onClick={handleSaveCollectionSettings}
              disabled={collectionSaved || !hasCollectionChanges}
            >
              {collectionSaved ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Tax Policy Card */}
        <Card>
          <CardHeader>
            <CardTitle>Tax Policy</CardTitle>
            <CardDescription>
              Choose who calculates tax, and how syncs react when the data they
              need is missing. Applies per deal.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <TaxPolicyGroup
              label="Tax calculation"
              helpText="Applies to new subscriptions and invoices only — Stripe cannot switch existing ones."
              value={taxSettings.mode}
              onChange={(v) => setTaxSettings({ ...taxSettings, mode: v })}
              name="tax_mode"
              options={TAX_MODE_OPTIONS}
            />

            {taxSettings.mode === 'automatic' && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                <p className="font-medium">Before turning this on</p>
                <ul className="mt-1 list-disc space-y-1 pl-4">
                  <li>
                    Stripe Tax must be active on your Stripe account, with a
                    registration in each jurisdiction you collect in — otherwise
                    every sync will fail.{' '}
                    <a
                      href="https://dashboard.stripe.com/settings/tax"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      Stripe Tax settings
                    </a>
                  </li>
                  <li>
                    Every line uses your Stripe account's default product tax
                    code. If you sell a mix of goods that are taxed differently,
                    rates may be wrong in jurisdictions that distinguish them.
                  </li>
                </ul>
              </div>
            )}

            <TaxPolicyGroup
              label="Missing Tax ID"
              helpText="When a deal's company has no tax IDs in HubSpot. Applies in both modes — Stripe Tax uses tax IDs for reverse charge and VAT validation."
              value={taxSettings.on_missing_tax_id}
              onChange={(v) => setTaxSettings({ ...taxSettings, on_missing_tax_id: v })}
              name="on_missing_tax_id"
            />

            {taxSettings.mode === 'manual' ? (
              <TaxPolicyGroup
                label="Missing Tax Rate"
                helpText="When a line item has no tax rate in HubSpot."
                value={taxSettings.on_missing_tax_rate}
                onChange={(v) => setTaxSettings({ ...taxSettings, on_missing_tax_rate: v })}
                name="on_missing_tax_rate"
              />
            ) : (
              <TaxPolicyGroup
                label="Missing customer location"
                helpText="When neither the deal's contact nor its company has an address in HubSpot, and the Stripe customer has none either. Stripe Tax cannot calculate without one."
                value={taxSettings.on_missing_customer_location}
                onChange={(v) =>
                  setTaxSettings({ ...taxSettings, on_missing_customer_location: v })
                }
                name="on_missing_customer_location"
                options={LOCATION_POLICY_OPTIONS}
              />
            )}

            <Button
              onClick={handleSaveTaxSettings}
              disabled={taxSaved || !hasTaxChanges}
            >
              {taxSaved ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Sync Trigger Stages Card */}
        <Card>
          <CardHeader>
            <CardTitle>Sync Trigger Stages</CardTitle>
            <CardDescription>
              Deals are synced to Stripe when their HubSpot stage matches one of the stages you select here.
              Defaults to "Closed Won".
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pipelinesLoading && (
              <p className="text-sm text-muted-foreground">Loading pipelines from HubSpot…</p>
            )}
            {pipelinesError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {pipelinesError}{' '}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => {
                      setPipelinesLoading(true);
                      setPipelinesError(null);
                      apiClient.getHubSpotPipelines()
                        .then((data) => setPipelines(data))
                        .catch(() => setPipelinesError("Couldn't load HubSpot pipelines. Verify your HubSpot API key, then retry."))
                        .finally(() => setPipelinesLoading(false));
                    }}
                  >
                    Retry
                  </button>
                </AlertDescription>
              </Alert>
            )}
            {!pipelinesLoading && !pipelinesError && pipelines && (() => {
              const stageLabelById = new Map<string, string>();
              pipelines.forEach((p) => p.stages.forEach((s) => stageLabelById.set(s.stage_id, s.stage_label)));
              const selectedLabels = allowedStages.map((id) => stageLabelById.get(id) ?? id);
              const triggerLabel = selectedLabels.length > 0 ? selectedLabels.join(', ') : 'No stages selected';
              return (
                <Collapsible>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="group flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                    >
                      <span className="truncate text-left">{triggerLabel}</span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 space-y-3 rounded-md border border-input p-3">
                    {pipelines.map((pipeline) => (
                      <div key={pipeline.pipeline_id} className="space-y-2">
                        <Label className="text-sm font-semibold">{pipeline.pipeline_label}</Label>
                        <div className="space-y-1 pl-2">
                          {pipeline.stages.map((stage) => {
                            const checked = allowedStages.includes(stage.stage_id);
                            return (
                              <label key={stage.stage_id} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setAllowedStages([...allowedStages, stage.stage_id]);
                                    } else {
                                      setAllowedStages(allowedStages.filter((s) => s !== stage.stage_id));
                                    }
                                  }}
                                />
                                <span>{stage.stage_label}</span>
                                <span className="text-xs text-muted-foreground">({stage.stage_id})</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              );
            })()}
            {allowedStages.length === 0 && (
              <p className="text-xs text-destructive">Select at least one stage.</p>
            )}
            <Button
              onClick={handleSaveAllowedStages}
              disabled={
                stagesSaved
                || !hasStageChanges
                || allowedStages.length === 0
                || pipelinesLoading
                || !!pipelinesError
              }
            >
              {stagesSaved ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Primary Contact Selection Card */}
        <Card>
          <CardHeader>
            <CardTitle>Primary Contact Selection</CardTitle>
            <CardDescription>
              Enter the HubSpot association labels that identify your billing contact, highest priority first. If a deal's contacts have no matching label, the connector uses the first associated contact.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {primaryContactRoles.map((role, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-5 text-right shrink-0">{index + 1}.</span>
                  <Input
                    value={role}
                    onChange={(e) => {
                      const updated = [...primaryContactRoles];
                      updated[index] = e.target.value;
                      setPrimaryContactRoles(updated);
                    }}
                    placeholder="e.g. Billing Contact"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPrimaryContactRoles(primaryContactRoles.filter((_, i) => i !== index))}
                    aria-label="Remove role"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPrimaryContactRoles([...primaryContactRoles, ''])}
            >
              Add role
            </Button>
            <div className="pt-2">
              <Button
                onClick={handleSaveContactRoles}
                disabled={contactRolesSaved || !hasContactRolesChanges}
              >
                {contactRolesSaved ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Saved
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

          </TabsContent>

          {/* ============ Connections tab ============ */}
          <TabsContent value="connections" className="space-y-6">

        {/* Credential Environment (read-only, driven by sidebar toggle) */}
        <Card>
          <CardHeader>
            <CardTitle>Credential Environment</CardTitle>
            <CardDescription>
              Switch environments using the toggle in the sidebar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Badge
              variant="secondary"
              className={
                isTestMode
                  ? 'bg-amber-100 text-amber-800 border-amber-200'
                  : 'bg-green-100 text-green-800 border-green-200'
              }
            >
              {isTestMode ? 'Test Mode' : 'Production Mode'}
            </Badge>
          </CardContent>
        </Card>

        {/* Install Stripe App Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                <StripeIcon className="w-5 h-5" />
              </div>
              Install Stripe App
            </CardTitle>
            <CardDescription>
              Connect your Stripe account with OAuth to enable subscription syncing.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {stripeStatusError && (
              <Alert variant="destructive">
                <AlertDescription>{stripeStatusError}</AlertDescription>
              </Alert>
            )}
            {stripeConnectError && (
              <Alert variant="destructive">
                <AlertDescription>{stripeConnectError}</AlertDescription>
              </Alert>
            )}
            {stripeOauthStatus?.legacy_key_present && (
              <Alert>
                <AlertDescription>
                  A legacy Stripe API key is on file. Installing the app via OAuth
                  replaces it.
                </AlertDescription>
              </Alert>
            )}
            {stripeOauthStatus?.connected ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>
                    Connected to Stripe account{' '}
                    <span className="font-mono">{stripeOauthStatus.stripe_account_id}</span>
                    {stripeOauthStatus.environment && <> ({stripeOauthStatus.environment})</>}
                  </span>
                </div>
                {stripeOauthStatus.revoked && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Your Stripe connection was uninstalled.
                    </AlertDescription>
                  </Alert>
                )}
                {stripeOauthStatus.token_health !== 'ok' && !stripeOauthStatus.revoked && (
                  <Alert className="border-amber-300 bg-amber-50 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Stripe connection health: {stripeOauthStatus.token_health}. You may
                      need to reinstall.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2 pt-1">
                  <Label htmlFor="stripe-mode">Environment to connect</Label>
                  <Select value={stripeMode} onValueChange={(v) => setStripeMode(v as 'live' | 'test' | 'sandbox')}>
                    <SelectTrigger id="stripe-mode" className="w-48">
                      <SelectValue placeholder="Select environment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="test">Test</SelectItem>
                      <SelectItem value="sandbox">Sandbox</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={handleConnectStripe} disabled={stripeConnecting}>
                  {stripeConnecting ? 'Waiting for Stripe…' : 'Reinstall'}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="space-y-2 pt-1">
                  <Label htmlFor="stripe-mode">Environment to connect</Label>
                  <Select value={stripeMode} onValueChange={(v) => setStripeMode(v as 'live' | 'test' | 'sandbox')}>
                    <SelectTrigger id="stripe-mode" className="w-48">
                      <SelectValue placeholder="Select environment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="live">Live</SelectItem>
                      <SelectItem value="test">Test</SelectItem>
                      <SelectItem value="sandbox">Sandbox</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleConnectStripe} disabled={stripeConnecting}>
                  {stripeConnecting ? 'Waiting for Stripe…' : 'Connect Stripe'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stripe API Key Card (legacy) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                <StripeIcon className="w-5 h-5" />
              </div>
              Stripe API Key
            </CardTitle>
            <CardDescription>
              Enter your Stripe Restricted Access API key to enable subscription syncing.
              Deprecated in favor of installing the Stripe App above.
            </CardDescription>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 cursor-help w-fit">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    <span>Please ensure you only enter Stripe Restricted Access keys</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs space-y-1">
                  <p>Restricted keys should have read and write access to Core and Billing objects</p>
                  <a
                    href="https://docs.stripe.com/keys/restricted-api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline block"
                  >
                    Learn more about restricted access keys here
                  </a>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardHeader>
          <CardContent className="space-y-4">
            {stripeError && (
              <Alert variant="destructive">
                <AlertDescription>{stripeError}</AlertDescription>
              </Alert>
            )}
            {stripeKeyWarnings.length > 0 && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Key saved, but it is missing read access to:{' '}
                  <span className="font-medium">{stripeKeyWarnings.join(', ')}</span>.
                  Syncing may fail for these resources. See{' '}
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-amber-800 underline"
                    onClick={() => setShowPermissions(true)}
                  >
                    required permissions
                  </Button>{' '}
                  to fix.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="stripe-key">API Key</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="stripe-key"
                    type={showStripeKey ? 'text' : 'password'}
                    value={stripeApiKey}
                    onChange={(e) => { setStripeApiKey(e.target.value); setStripeError(null); }}
                    placeholder={settings?.stripe_key_preview || (isTestMode ? 'rk_test_... or sk_test_...' : 'rk_live_... or sk_live_...')}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowStripeKey(!showStripeKey)}
                  >
                    {showStripeKey ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <Button onClick={handleSaveStripeKey} disabled={stripeSaved || !stripeApiKey}>
                  {stripeSaved ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Saved
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>
            <div className="pt-1">
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => setShowPermissions((v) => !v)}
              >
                {showPermissions ? 'Hide required permissions' : 'View required permissions'}
              </Button>
            </div>
            {showPermissions && (
              // KEEP IN SYNC with STRIPE_READ_PROBES in
              // backend/clients/stripe_client.py
              <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-2">
                <p className="font-medium">Required Stripe permissions (Restricted Access key)</p>
                <ul className="space-y-1">
                  <li>
                    <span className="font-medium">Account / Connect</span> — Read · includes{' '}
                    <span className="font-mono text-xs">accounts_kyc_basic_read</span>{' '}
                    (&ldquo;Basic Business Contact Information&rdquo;)
                  </li>
                  <li>
                    <span className="font-medium">Core</span> — Read &amp; write: Customers, Products, Prices
                  </li>
                  <li>
                    <span className="font-medium">Billing</span> — Read &amp; write: Subscriptions, Invoices, Tax rates
                  </li>
                </ul>
                <p className="text-muted-foreground">
                  Tip: a standard secret key (<span className="font-mono text-xs">sk_…</span>) has
                  full access if you prefer not to configure scopes.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* HubSpot Connection Card */}
        <Card>
          <CardHeader>
            <CardTitle>HubSpot Connection</CardTitle>
            <CardDescription>
              Connect your HubSpot account with OAuth to enable deal syncing.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {statusError && (
              <Alert variant="destructive">
                <AlertDescription>{statusError}</AlertDescription>
              </Alert>
            )}
            {connectError && (
              <Alert variant="destructive">
                <AlertDescription>{connectError}</AlertDescription>
              </Alert>
            )}
            {hubspotStatus?.connected ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-600" />
                  <span>
                    Connected to portal{' '}
                    <span className="font-mono">{hubspotStatus.portal_id}</span>
                    {hubspotStatus.user_email && <> by {hubspotStatus.user_email}</>}
                  </span>
                </div>
                {hubspotStatus.missing_scopes.length > 0 && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      HubSpot did not grant these required permissions:{' '}
                      {hubspotStatus.missing_scopes.join(', ')}. Reconnect and approve
                      all requested permissions.
                    </AlertDescription>
                  </Alert>
                )}
                <Button variant="outline" onClick={handleConnectHubspot} disabled={connecting}>
                  {connecting ? 'Waiting for HubSpot…' : 'Reconnect'}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {hubspotStatus?.legacy_key_present && (
                  <Alert>
                    <AlertDescription>
                      You're currently using a legacy private-app token. Connect with
                      OAuth below — the legacy token keeps working until you do.
                    </AlertDescription>
                  </Alert>
                )}
                <Button onClick={handleConnectHubspot} disabled={connecting}>
                  {connecting ? 'Waiting for HubSpot…' : 'Connect HubSpot'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Stripe App Connection Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                <Key className="w-5 h-5" />
              </div>
              Stripe App Connection
            </CardTitle>
            <CardDescription>
              Generate an API key to connect the Finrite Stripe App. Paste this key into
              the app's Settings view in the Stripe Dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {apiKeyError && (
              <Alert variant="destructive">
                <AlertDescription>{apiKeyError}</AlertDescription>
              </Alert>
            )}

            {generatedApiKey && (
              <Alert>
                <AlertDescription className="space-y-2">
                  <p className="font-semibold text-amber-600">This key will only be shown once. Copy it now.</p>
                  <div className="flex gap-2 items-center">
                    <Input
                      readOnly
                      value={generatedApiKey}
                      className="font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyApiKey}
                    >
                      {apiKeyCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {settings?.stripe_app_api_key_preview && !generatedApiKey && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Current key:</span>
                <span className="font-mono text-sm">{settings.stripe_app_api_key_preview}</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleGenerateApiKey}
                disabled={apiKeyGenerating}
              >
                <Key className="mr-2 h-4 w-4" />
                {settings?.stripe_app_api_key_preview ? 'Regenerate Key' : 'Generate API Key'}
              </Button>
              {settings?.stripe_app_api_key_preview && (
                <Button
                  variant="destructive"
                  onClick={handleRevokeApiKey}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Revoke Key
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
